import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAuthStore } from "@/store/auth";

// Lazy load pages for code splitting
import { lazy, Suspense } from "react";

// Layout
const MainLayout = lazy(() => import("@/components/layout/MainLayout"));
const DeskLayout = lazy(() => import("@/components/layout/DeskLayout"));
const DeskPage = lazy(() => import("@/pages/helpdesk/DeskPage"));

// Auth pages
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const AcceptInvitePage = lazy(() => import("@/pages/auth/AcceptInvitePage"));
const ForcePasswordChangePage = lazy(() => import("@/pages/auth/ForcePasswordChangePage"));
const ForceMfaSetupPage = lazy(() => import("@/pages/auth/ForceMfaSetupPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/pages/auth/VerifyEmailPage"));

// Error pages
const AccessDeniedPage = lazy(() => import("@/pages/errors/AccessDeniedPage"));
const NotFoundPage = lazy(() => import("@/pages/errors/NotFoundPage"));

// Protected pages
const DashboardPage = lazy(() => import("@/pages/dashboard/DashboardPage"));
const CompaniesPage = lazy(() => import("@/pages/companies/CompaniesPage"));
const CompanyFormPage = lazy(() => import("@/pages/companies/CompanyFormPage"));
const KnowledgeBasesPage = lazy(() => import("@/pages/knowledge-bases/KnowledgeBasesPage"));
const KnowledgeBaseFormPage = lazy(() => import("@/pages/knowledge-bases/KnowledgeBaseFormPage"));
const KnowledgeBaseDetailPage = lazy(() => import("@/pages/knowledge-bases/KnowledgeBaseDetailPage"));
const SearchPage = lazy(() => import("@/pages/search/SearchPage"));
const APIKeysPage = lazy(() => import("@/pages/api-keys/APIKeysPage"));
const APIKeyFormPage = lazy(() => import("@/pages/api-keys/APIKeyFormPage"));
const UsersPage = lazy(() => import("@/pages/users/UsersPage"));
const RolesPage = lazy(() => import("@/pages/roles/RolesPage"));
const RoleFormPage = lazy(() => import("@/pages/roles/RoleFormPage"));
const GroupsPage = lazy(() => import("@/pages/groups/GroupsPage"));
const AuditLogsPage = lazy(() => import("@/pages/audit/AuditLogsPage"));
const DocumentsPage = lazy(() => import("@/pages/documents/DocumentsPage"));
const DocumentDetailPage = lazy(() => import("@/pages/documents/DocumentDetailPage"));
const ProfilePage = lazy(() => import("@/pages/profile/ProfilePage"));
const CredentialsPage = lazy(() => import("@/pages/credentials/CredentialsPage"));
const CredentialFormPage = lazy(() => import("@/pages/credentials/CredentialFormPage"));
const CredentialTypesPage = lazy(() => import("@/pages/credentials/CredentialTypesPage"));
const CredentialTypeFormPage = lazy(() => import("@/pages/credentials/CredentialTypeFormPage"));
const AgentsPage = lazy(() => import("@/pages/agents/AgentsPage"));
const AgentWizardPage = lazy(() => import("@/pages/agents/AgentWizardPage"));
const SurveysPage = lazy(() => import("@/pages/surveys/SurveysPage"));
const SurveyEditorPage = lazy(() => import("@/pages/surveys/SurveyEditorPage"));

// Guided Flows pages
const GuidedFlowsPage = lazy(() => import("@/pages/flows/GuidedFlowsPage"));
const GuidedFlowBuilderPage = lazy(() => import("@/pages/flows/GuidedFlowBuilderPage"));

// Pipeline pages
const PipelinesPage = lazy(() => import("@/pages/pipelines/PipelinesPage"));
const PipelineExecutionsPage = lazy(() => import("@/pages/pipelines/PipelineExecutionsPage"));
const PipelineBuilderPage = lazy(() => import("@/pages/pipelines/PipelineBuilderPage"));
const PipelineMonitorPage = lazy(() => import("@/pages/pipelines/PipelineMonitorPage"));
const PipelineAIBuilderPage = lazy(() => import("@/pages/pipelines/PipelineAIBuilderPage"));

// Experiment pages
const ExperimentsPage = lazy(() => import("@/pages/experiments/ExperimentsPage"));
const ExperimentFormPage = lazy(() => import("@/pages/experiments/ExperimentFormPage"));
const ExperimentDetailPage = lazy(() => import("@/pages/experiments/ExperimentDetailPage"));
const CurationWorkspacePage = lazy(() => import("@/pages/curation/CurationWorkspacePage"));
const EvalDashboardPage = lazy(() => import("@/pages/curation/EvalDashboardPage"));
const RolloutPage = lazy(() => import("@/pages/curation/RolloutPage"));
const GovernancePage = lazy(() => import("@/pages/curation/GovernancePage"));

// Pricing
const ModelPricingPage = lazy(() => import("@/pages/pricing/ModelPricingPage"));

// Template Categories
const TemplateCategoriesPage = lazy(() => import("@/pages/admin/TemplateCategoriesPage"));

// Access Inspector
const AccessInspectorPage = lazy(() => import("@/pages/admin/AccessInspectorPage"));

// Contact Hub pages
const ContactsPage = lazy(() => import("@/pages/contacts/ContactsPage"));
const ContactDetailPage = lazy(() => import("@/pages/contacts/ContactDetailPage"));
const ContactFormPage = lazy(() => import("@/pages/contacts/ContactFormPage"));
const ContactMergePage = lazy(() => import("@/pages/contacts/ContactMergePage"));
const ContactImportPage = lazy(() => import("@/pages/contacts/ContactImportPage"));
const PortfolioPage = lazy(() => import("@/pages/contacts/PortfolioPage"));
const TeamPortfolioPage = lazy(() => import("@/pages/contacts/TeamPortfolioPage"));
const HierarchyPage = lazy(() => import("@/pages/admin/HierarchyPage"));
const MyAvailabilityPage = lazy(() => import("@/pages/helpdesk/MyAvailabilityPage"));
const SegmentsPage = lazy(() => import("@/pages/segments/SegmentsPage"));
const SegmentFormPage = lazy(() => import("@/pages/segments/SegmentFormPage"));
const CustomAttributesPage = lazy(() => import("@/pages/custom-attributes/CustomAttributesPage"));

// Agent Router pages
const RouterListPage = lazy(() => import("@/pages/router/RouterListPage"));
const RouterConfigPage = lazy(() => import("@/pages/router/RouterConfigPage"));
const RouterTestPage = lazy(() => import("@/pages/router/RouterTestPage"));
const RouterDashboardPage = lazy(() => import("@/pages/router/RouterDashboardPage"));

// Helpdesk pages
const InboxPage = lazy(() => import("@/pages/helpdesk/InboxPage"));
const SquadsPage = lazy(() => import("@/pages/helpdesk/SquadsPage"));
const TagsPage = lazy(() => import("@/pages/helpdesk/TagsPage"));
const TicketTypesPage = lazy(() => import("@/pages/tickets/TicketTypesPage"));
const BoardsListPage = lazy(() => import("@/pages/tickets/BoardsListPage"));
const BoardPage = lazy(() => import("@/pages/tickets/BoardPage"));
const TicketTemplatesPage = lazy(() => import("@/pages/tickets/TicketTemplatesPage"));
const TicketWorkflowsListPage = lazy(() => import("@/pages/tickets/TicketWorkflowsListPage"));
const TicketWorkflowEditorPage = lazy(() => import("@/pages/tickets/TicketWorkflowEditorPage"));
const TicketAIPanelPage = lazy(() => import("@/pages/tickets/TicketAIPanelPage"));
const TicketActivityPage = lazy(() => import("@/pages/tickets/TicketActivityPage"));
const TicketLinksPage = lazy(() => import("@/pages/tickets/TicketLinksPage"));
const TicketsDashboardPage = lazy(() => import("@/pages/tickets/TicketsDashboardPage"));
const TicketsInternalPage = lazy(() => import("@/pages/tickets/TicketsInternalPage"));
const TicketDetailPage = lazy(() => import("@/pages/tickets/TicketDetailPage"));

function LegacyInternalRedirect() {
  // /tickets/internal/:threadId → previously was the dedicated internal-task
  // detail. Now everything is /tickets/:ticketId. Forward the param.
  const m = window.location.pathname.match(/\/tickets\/internal\/([^/?#]+)/);
  const id = m ? m[1] : "";
  return <Navigate to={id ? `/tickets/${id}` : "/tickets/internal"} replace />;
}
const QuickRepliesPage = lazy(() => import("@/pages/helpdesk/QuickRepliesPage"));
const SLAPoliciesPage = lazy(() => import("@/pages/helpdesk/SLAPoliciesPage"));
const AutomationRulesPage = lazy(() => import("@/pages/helpdesk/AutomationRulesPage"));
const ChannelsPage = lazy(() => import("@/pages/channels/ChannelsPage"));
const QuickActionsPage = lazy(() => import("@/pages/helpdesk/QuickActionsPage"));
const BusinessHoursPage = lazy(() => import("@/pages/helpdesk/BusinessHoursPage"));
const HelpdeskSettingsPage = lazy(() => import("@/pages/helpdesk/HelpdeskSettingsPage"));
const HelpdeskSearchPage = lazy(() => import("@/pages/helpdesk/SearchPage"));
const HelpdeskReportsPage = lazy(() => import("@/pages/helpdesk/ReportsPage"));
const LiveMonitorPage = lazy(() => import("@/pages/helpdesk/LiveMonitorPage"));
const AIConfigPage = lazy(() => import("@/pages/helpdesk/AIConfigPage"));
const MacrosPage = lazy(() => import("@/pages/helpdesk/MacrosPage"));

// Quality pages
// KB Connections
const KBConnectionsPage = lazy(() => import("@/pages/kb-connections/KBConnectionsPage"));

const QualityDashboardPage = lazy(() => import("@/pages/quality/QualityDashboardPage"));
const EvaluationsPage = lazy(() => import("@/pages/quality/EvaluationsPage"));
const EvaluationDetailPage = lazy(() => import("@/pages/quality/EvaluationDetailPage"));
const QualityRulesPage = lazy(() => import("@/pages/quality/QualityRulesPage"));
const KBGapsPage = lazy(() => import("@/pages/quality/KBGapsPage"));
const AlertSettingsPage = lazy(() => import("@/pages/quality/AlertSettingsPage"));
const QualitySettingsPage = lazy(() => import("@/pages/quality/QualitySettingsPage"));

// Analytics pages
const AnalyticsDashboardPage = lazy(() => import("@/pages/analytics/AnalyticsDashboardPage"));
const AgentAnalyticsPage = lazy(() => import("@/pages/analytics/AgentAnalyticsPage"));
const AnalyticsLivePage = lazy(() => import("@/pages/analytics/AnalyticsLivePage"));
const ConversationExplorerPage = lazy(() => import("@/pages/analytics/ConversationExplorerPage"));
const ConversationDetailPage = lazy(() => import("@/pages/analytics/ConversationDetailPage"));
const AlertsConfigPage = lazy(() => import("@/pages/analytics/AlertsConfigPage"));
const UsageStatementPage = lazy(() => import("@/pages/analytics/UsageStatementPage"));

// WhatsApp Business pages
const WhatsAppTemplatesPage = lazy(() => import("@/pages/whatsapp/WhatsAppTemplatesPage"));
const WhatsAppTemplateFormPage = lazy(() => import("@/pages/whatsapp/WhatsAppTemplateFormPage"));
const WhatsAppCampaignsPage = lazy(() => import("@/pages/whatsapp/WhatsAppCampaignsPage"));
const WhatsAppCampaignFormPage = lazy(() => import("@/pages/whatsapp/WhatsAppCampaignFormPage"));
const WhatsAppCampaignDetailPage = lazy(() => import("@/pages/whatsapp/WhatsAppCampaignDetailPage"));
const WhatsAppSendPage = lazy(() => import("@/pages/whatsapp/WhatsAppSendPage"));
const WhatsAppAnalyticsPage = lazy(() => import("@/pages/whatsapp/WhatsAppAnalyticsPage"));
const WhatsAppSettingsPage = lazy(() => import("@/pages/whatsapp/WhatsAppSettingsPage"));

// Tools pages
const PdfEditorPage = lazy(() => import("@/pages/tools/PdfEditorPage"));

// Setup page
const SetupPage = lazy(() => import("@/pages/setup/SetupPage"));

function LoadingSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
    </div>
  );
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  permissions?: string[];
}

function ProtectedRoute({ children, permissions }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasAnyPermission } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions if specified
  if (permissions && permissions.length > 0 && !hasAnyPermission(permissions)) {
    return <AccessDeniedPage />;
  }

  return <>{children}</>;
}

interface PermissionRouteProps {
  children: React.ReactNode;
  permissions: string[];
}

function PermissionRoute({ children, permissions }: PermissionRouteProps) {
  const { hasAnyPermission } = useAuth();

  if (!hasAnyPermission(permissions)) {
    return <AccessDeniedPage />;
  }

  return <>{children}</>;
}

interface PublicRouteProps {
  children: React.ReactNode;
}

function PublicRoute({ children }: PublicRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <LandingRedirect />;
  }

  return <>{children}</>;
}

/** Post-login landing: desk agents (helpdesk:desk-home) go straight to the lean Desk. */
function LandingRedirect() {
  const perms = useAuthStore((s) => s.user?.permissions);
  const to =
    Array.isArray(perms) && perms.includes("helpdesk:desk-home")
      ? "/desk"
      : "/dashboard";
  return <Navigate to={to} replace />;
}

export function AppRouter() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Setup route - accessible without auth, handles its own redirects */}
        <Route path="/setup" element={<SetupPage />} />

        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />

        {/* No auth wrapper - accessible regardless of auth state */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Accept invitation - accessible without auth */}
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />

        {/* Forced action routes (protected, no layout) */}
        <Route
          path="/force-password-change"
          element={
            <ProtectedRoute>
              <ForcePasswordChangePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/force-mfa-setup"
          element={
            <ProtectedRoute>
              <ForceMfaSetupPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes with layout */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard - always accessible to authenticated users */}
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Profile - always accessible to authenticated users */}
          <Route path="/profile" element={<ProfilePage />} />

          {/* Helpdesk routes */}
          <Route path="/inbox" element={
            <PermissionRoute permissions={["helpdesk:read"]}>
              <InboxPage />
            </PermissionRoute>
          } />
          <Route path="/inbox/:threadId" element={
            <PermissionRoute permissions={["helpdesk:read"]}>
              <InboxPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/squads" element={
            <PermissionRoute permissions={["helpdesk-squads:read"]}>
              <SquadsPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/tags" element={
            <PermissionRoute permissions={["helpdesk-tags:read"]}>
              <TagsPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/quick-replies" element={
            <PermissionRoute permissions={["helpdesk:read"]}>
              <QuickRepliesPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/sla" element={
            <PermissionRoute permissions={["helpdesk-sla:read"]}>
              <SLAPoliciesPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/automations" element={
            <PermissionRoute permissions={["helpdesk-automation:read"]}>
              <AutomationRulesPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/channels" element={
            <PermissionRoute permissions={["channels:read"]}>
              <ChannelsPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/quick-actions" element={
            <PermissionRoute permissions={["helpdesk-macros:read"]}>
              <QuickActionsPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/business-hours" element={
            <PermissionRoute permissions={["helpdesk-business-hours:read"]}>
              <BusinessHoursPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/settings" element={
            <PermissionRoute permissions={["helpdesk:read"]}>
              <HelpdeskSettingsPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/search" element={
            <PermissionRoute permissions={["helpdesk:read"]}>
              <HelpdeskSearchPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/reports" element={
            <PermissionRoute permissions={["helpdesk-reports:read"]}>
              <HelpdeskReportsPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/live" element={
            <PermissionRoute permissions={["helpdesk:read"]}>
              <LiveMonitorPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/macros" element={
            <PermissionRoute permissions={["helpdesk-macros:read"]}>
              <MacrosPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/ai-config" element={
            <PermissionRoute permissions={["helpdesk-ai:config"]}>
              <AIConfigPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/types" element={
            <PermissionRoute permissions={["tickets:read"]}>
              <TicketTypesPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/boards" element={
            <PermissionRoute permissions={["tickets:read"]}>
              <BoardsListPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/boards/:boardId" element={
            <PermissionRoute permissions={["tickets:read"]}>
              <BoardPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/types/:ticketTypeId/templates" element={
            <PermissionRoute permissions={["tickets:read"]}>
              <TicketTemplatesPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/types/:ticketTypeId/workflows" element={
            <PermissionRoute permissions={["tickets:read"]}>
              <TicketWorkflowsListPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/types/:ticketTypeId/workflows/:workflowId" element={
            <PermissionRoute permissions={["tickets:read"]}>
              <TicketWorkflowEditorPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/dashboard" element={
            <PermissionRoute permissions={["helpdesk:read"]}>
              <TicketsDashboardPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/internal" element={
            <PermissionRoute permissions={["tickets:read"]}>
              <TicketsInternalPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/:ticketId" element={
            <PermissionRoute permissions={["tickets:read"]}>
              <TicketDetailPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/:ticketId/ai" element={
            <PermissionRoute permissions={["helpdesk-ai:write"]}>
              <TicketAIPanelPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/:ticketId/activity" element={
            <PermissionRoute permissions={["tickets:read"]}>
              <TicketActivityPage />
            </PermissionRoute>
          } />
          <Route path="/tickets/:ticketId/links" element={
            <PermissionRoute permissions={["tickets:read"]}>
              <TicketLinksPage />
            </PermissionRoute>
          } />
          {/* Legacy URL: /tickets/internal/:id → /tickets/:id */}
          <Route path="/tickets/internal/:threadId" element={<LegacyInternalRedirect />} />

          {/* Legacy redirect — channels moved under helpdesk */}
          <Route path="/channels" element={<Navigate to="/helpdesk/channels" replace />} />

          {/* AI Agents routes */}
          <Route path="/agents" element={
            <PermissionRoute permissions={["agents:read"]}>
              <AgentsPage />
            </PermissionRoute>
          } />
          <Route path="/agents/new" element={
            <PermissionRoute permissions={["agents:write"]}>
              <AgentWizardPage />
            </PermissionRoute>
          } />
          <Route path="/agents/:agentId/edit" element={
            <PermissionRoute permissions={["agents:write"]}>
              <AgentWizardPage />
            </PermissionRoute>
          } />

          {/* Surveys routes */}
          <Route path="/surveys" element={
            <PermissionRoute permissions={["surveys:view"]}>
              <SurveysPage />
            </PermissionRoute>
          } />
          <Route path="/surveys/new" element={
            <PermissionRoute permissions={["surveys:create"]}>
              <SurveyEditorPage />
            </PermissionRoute>
          } />
          <Route path="/surveys/:surveyId/edit" element={
            <PermissionRoute permissions={["surveys:view"]}>
              <SurveyEditorPage />
            </PermissionRoute>
          } />

          {/* Guided Flows routes */}
          <Route path="/flows" element={
            <PermissionRoute permissions={["guided-flows:read"]}>
              <GuidedFlowsPage />
            </PermissionRoute>
          } />
          <Route path="/flows/new" element={
            <PermissionRoute permissions={["guided-flows:write"]}>
              <GuidedFlowBuilderPage />
            </PermissionRoute>
          } />
          <Route path="/flows/:flowId/edit" element={
            <PermissionRoute permissions={["guided-flows:write"]}>
              <GuidedFlowBuilderPage />
            </PermissionRoute>
          } />

          {/* Pipeline routes */}
          <Route path="/pipelines" element={
            <PermissionRoute permissions={["pipelines:read"]}>
              <PipelinesPage />
            </PermissionRoute>
          } />
          <Route path="/pipelines/executions" element={
            <PermissionRoute permissions={["pipelines:read"]}>
              <PipelineExecutionsPage />
            </PermissionRoute>
          } />
          <Route path="/pipelines/new" element={
            <PermissionRoute permissions={["pipelines:write"]}>
              <PipelineBuilderPage />
            </PermissionRoute>
          } />
          <Route path="/pipelines/:pipelineId/edit" element={
            <PermissionRoute permissions={["pipelines:write"]}>
              <PipelineBuilderPage />
            </PermissionRoute>
          } />
          <Route path="/pipelines/ai-builder" element={
            <PermissionRoute permissions={["pipelines:write"]}>
              <PipelineAIBuilderPage />
            </PermissionRoute>
          } />
          <Route path="/pipelines/:pipelineId/executions" element={
            <PermissionRoute permissions={["pipelines:read"]}>
              <PipelineExecutionsPage />
            </PermissionRoute>
          } />
          <Route path="/pipelines/:pipelineId/executions/:executionId" element={
            <PermissionRoute permissions={["pipelines:read"]}>
              <PipelineMonitorPage />
            </PermissionRoute>
          } />

          {/* Contact Hub routes */}
          <Route path="/contacts" element={
            <PermissionRoute permissions={["contacts:read"]}>
              <ContactsPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/new" element={
            <PermissionRoute permissions={["contacts:write"]}>
              <ContactFormPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/import" element={
            <PermissionRoute permissions={["contacts:import"]}>
              <ContactImportPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/portfolio" element={
            <PermissionRoute permissions={["contact_ownership:read"]}>
              <PortfolioPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/portfolio/team" element={
            <PermissionRoute permissions={["contact_ownership:write"]}>
              <TeamPortfolioPage />
            </PermissionRoute>
          } />
          <Route path="/admin/hierarchy" element={
            <PermissionRoute permissions={["user_management:read"]}>
              <HierarchyPage />
            </PermissionRoute>
          } />
          <Route path="/helpdesk/my-availability" element={
            <PermissionRoute permissions={["user_business_hours:read"]}>
              <MyAvailabilityPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/segments" element={
            <PermissionRoute permissions={["segments:read"]}>
              <SegmentsPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/segments/new" element={
            <PermissionRoute permissions={["segments:write"]}>
              <SegmentFormPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/segments/:segmentId/edit" element={
            <PermissionRoute permissions={["segments:write"]}>
              <SegmentFormPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/attributes" element={
            <PermissionRoute permissions={["custom-attributes:read"]}>
              <CustomAttributesPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/:contactId" element={
            <PermissionRoute permissions={["contacts:read"]}>
              <ContactDetailPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/:contactId/edit" element={
            <PermissionRoute permissions={["contacts:write"]}>
              <ContactFormPage />
            </PermissionRoute>
          } />
          <Route path="/contacts/:contactId/merge" element={
            <PermissionRoute permissions={["contacts:merge"]}>
              <ContactMergePage />
            </PermissionRoute>
          } />

          {/* Agent Router routes */}
          <Route path="/router" element={
            <PermissionRoute permissions={["router:read"]}>
              <RouterListPage />
            </PermissionRoute>
          } />
          <Route path="/router/new" element={
            <PermissionRoute permissions={["router:write"]}>
              <RouterConfigPage />
            </PermissionRoute>
          } />
          <Route path="/router/:routerId" element={
            <PermissionRoute permissions={["router:read"]}>
              <RouterConfigPage />
            </PermissionRoute>
          } />
          <Route path="/router/:routerId/test" element={
            <PermissionRoute permissions={["router:test"]}>
              <RouterTestPage />
            </PermissionRoute>
          } />
          <Route path="/router/:routerId/analytics" element={
            <PermissionRoute permissions={["router:read"]}>
              <RouterDashboardPage />
            </PermissionRoute>
          } />

          {/* Experiment routes */}
          <Route path="/experiments" element={
            <PermissionRoute permissions={["experiments:read"]}>
              <ExperimentsPage />
            </PermissionRoute>
          } />
          <Route path="/experiments/new" element={
            <PermissionRoute permissions={["experiments:write"]}>
              <ExperimentFormPage />
            </PermissionRoute>
          } />
          <Route path="/experiments/:experimentId" element={
            <PermissionRoute permissions={["experiments:read"]}>
              <ExperimentDetailPage />
            </PermissionRoute>
          } />
          <Route path="/curation" element={
            <PermissionRoute permissions={["curation:read"]}>
              <CurationWorkspacePage />
            </PermissionRoute>
          } />
          <Route path="/curation/eval" element={
            <PermissionRoute permissions={["quality-eval:read"]}>
              <EvalDashboardPage />
            </PermissionRoute>
          } />
          <Route path="/curation/rollout" element={
            <PermissionRoute permissions={["rollout:read"]}>
              <RolloutPage />
            </PermissionRoute>
          } />
          <Route path="/curation/governance" element={
            <PermissionRoute permissions={["governance:read"]}>
              <GovernancePage />
            </PermissionRoute>
          } />
          <Route path="/experiments/:experimentId/edit" element={
            <PermissionRoute permissions={["experiments:write"]}>
              <ExperimentFormPage />
            </PermissionRoute>
          } />

          {/* Quality routes */}
          <Route path="/quality" element={
            <PermissionRoute permissions={["quality:read"]}>
              <QualityDashboardPage />
            </PermissionRoute>
          } />
          <Route path="/quality/evaluations" element={
            <PermissionRoute permissions={["quality:read"]}>
              <EvaluationsPage />
            </PermissionRoute>
          } />
          <Route path="/quality/evaluations/:evaluationId" element={
            <PermissionRoute permissions={["quality:read"]}>
              <EvaluationDetailPage />
            </PermissionRoute>
          } />
          <Route path="/quality/rules" element={
            <PermissionRoute permissions={["quality-rules:read"]}>
              <QualityRulesPage />
            </PermissionRoute>
          } />
          <Route path="/quality/kb-gaps" element={
            <PermissionRoute permissions={["quality:read"]}>
              <KBGapsPage />
            </PermissionRoute>
          } />
          <Route path="/quality/alerts" element={
            <PermissionRoute permissions={["quality:write"]}>
              <AlertSettingsPage />
            </PermissionRoute>
          } />
          <Route path="/quality/settings" element={
            <PermissionRoute permissions={["quality:write"]}>
              <QualitySettingsPage />
            </PermissionRoute>
          } />

          {/* Analytics routes */}
          <Route path="/analytics" element={
            <PermissionRoute permissions={["analytics:read"]}>
              <AnalyticsDashboardPage />
            </PermissionRoute>
          } />
          <Route path="/analytics/agents/:agentId" element={
            <PermissionRoute permissions={["analytics:read"]}>
              <AgentAnalyticsPage />
            </PermissionRoute>
          } />
          <Route path="/analytics/live" element={
            <PermissionRoute permissions={["analytics:read"]}>
              <AnalyticsLivePage />
            </PermissionRoute>
          } />
          <Route path="/analytics/conversations" element={
            <PermissionRoute permissions={["analytics:read"]}>
              <ConversationExplorerPage />
            </PermissionRoute>
          } />
          <Route path="/analytics/conversations/:sessionId" element={
            <PermissionRoute permissions={["analytics:read"]}>
              <ConversationDetailPage />
            </PermissionRoute>
          } />
          <Route path="/analytics/alerts" element={
            <PermissionRoute permissions={["analytics:write"]}>
              <AlertsConfigPage />
            </PermissionRoute>
          } />
          <Route path="/analytics/usage-statement" element={
            <PermissionRoute permissions={["analytics:read"]}>
              <UsageStatementPage />
            </PermissionRoute>
          } />

          {/* WhatsApp Business routes */}
          <Route path="/whatsapp/templates" element={
            <PermissionRoute permissions={["whatsapp:read"]}>
              <WhatsAppTemplatesPage />
            </PermissionRoute>
          } />
          <Route path="/whatsapp/templates/new" element={
            <PermissionRoute permissions={["whatsapp:write"]}>
              <WhatsAppTemplateFormPage />
            </PermissionRoute>
          } />
          <Route path="/whatsapp/templates/:templateId/edit" element={
            <PermissionRoute permissions={["whatsapp:write"]}>
              <WhatsAppTemplateFormPage />
            </PermissionRoute>
          } />
          <Route path="/whatsapp/campaigns" element={
            <PermissionRoute permissions={["whatsapp:read"]}>
              <WhatsAppCampaignsPage />
            </PermissionRoute>
          } />
          <Route path="/whatsapp/campaigns/new" element={
            <PermissionRoute permissions={["whatsapp:campaign"]}>
              <WhatsAppCampaignFormPage />
            </PermissionRoute>
          } />
          <Route path="/whatsapp/campaigns/:campaignId" element={
            <PermissionRoute permissions={["whatsapp:read"]}>
              <WhatsAppCampaignDetailPage />
            </PermissionRoute>
          } />
          <Route path="/whatsapp/campaigns/:campaignId/edit" element={
            <PermissionRoute permissions={["whatsapp:campaign"]}>
              <WhatsAppCampaignFormPage />
            </PermissionRoute>
          } />
          <Route path="/whatsapp/send" element={
            <PermissionRoute permissions={["whatsapp:send"]}>
              <WhatsAppSendPage />
            </PermissionRoute>
          } />
          <Route path="/whatsapp/analytics" element={
            <PermissionRoute permissions={["whatsapp:read"]}>
              <WhatsAppAnalyticsPage />
            </PermissionRoute>
          } />
          <Route path="/whatsapp/settings" element={
            <PermissionRoute permissions={["whatsapp:write"]}>
              <WhatsAppSettingsPage />
            </PermissionRoute>
          } />

          {/* Tools routes */}
          <Route path="/tools/pdf" element={<Navigate to="/tools/pdf-editor" replace />} />
          <Route path="/tools/pdf-editor" element={
            <PermissionRoute permissions={["tools:use"]}>
              <PdfEditorPage />
            </PermissionRoute>
          } />

          {/* Content routes */}
          <Route path="/kb-connections" element={
            <PermissionRoute permissions={["kb-connections:read"]}>
              <KBConnectionsPage />
            </PermissionRoute>
          } />
          <Route path="/knowledge-bases" element={
            <PermissionRoute permissions={["kb:read"]}>
              <KnowledgeBasesPage />
            </PermissionRoute>
          } />
          <Route path="/knowledge-bases/new" element={
            <PermissionRoute permissions={["kb:write"]}>
              <KnowledgeBaseFormPage />
            </PermissionRoute>
          } />
          <Route path="/knowledge-bases/:kbId/edit" element={
            <PermissionRoute permissions={["kb:write"]}>
              <KnowledgeBaseFormPage />
            </PermissionRoute>
          } />
          <Route path="/knowledge-bases/:kbId" element={
            <PermissionRoute permissions={["kb:read"]}>
              <KnowledgeBaseDetailPage />
            </PermissionRoute>
          } />
          <Route path="/documents" element={
            <PermissionRoute permissions={["documents:read"]}>
              <DocumentsPage />
            </PermissionRoute>
          } />
          <Route path="/documents/:docId" element={
            <PermissionRoute permissions={["documents:read"]}>
              <DocumentDetailPage />
            </PermissionRoute>
          } />
          <Route path="/search" element={
            <PermissionRoute permissions={["search:query"]}>
              <SearchPage />
            </PermissionRoute>
          } />

          {/* Administration routes */}
          <Route path="/users" element={
            <PermissionRoute permissions={["users:read"]}>
              <UsersPage />
            </PermissionRoute>
          } />
          <Route path="/groups" element={
            <PermissionRoute permissions={["groups:read"]}>
              <GroupsPage />
            </PermissionRoute>
          } />
          <Route path="/roles" element={
            <PermissionRoute permissions={["roles:read"]}>
              <RolesPage />
            </PermissionRoute>
          } />
          <Route path="/roles/new" element={
            <PermissionRoute permissions={["roles:write"]}>
              <RoleFormPage />
            </PermissionRoute>
          } />
          <Route path="/roles/:roleId/edit" element={
            <PermissionRoute permissions={["roles:write"]}>
              <RoleFormPage />
            </PermissionRoute>
          } />
          <Route path="/companies" element={
            <PermissionRoute permissions={["company:read"]}>
              <CompaniesPage />
            </PermissionRoute>
          } />
          <Route path="/companies/new" element={
            <PermissionRoute permissions={["company:manage"]}>
              <CompanyFormPage />
            </PermissionRoute>
          } />
          <Route path="/companies/:companyId/edit" element={
            <PermissionRoute permissions={["company:manage"]}>
              <CompanyFormPage />
            </PermissionRoute>
          } />

          {/* Security routes */}
          <Route path="/api-keys" element={
            <PermissionRoute permissions={["apikeys:read"]}>
              <APIKeysPage />
            </PermissionRoute>
          } />
          <Route path="/api-keys/new" element={
            <PermissionRoute permissions={["apikeys:write"]}>
              <APIKeyFormPage />
            </PermissionRoute>
          } />
          <Route path="/api-keys/:keyId/edit" element={
            <PermissionRoute permissions={["apikeys:write"]}>
              <APIKeyFormPage />
            </PermissionRoute>
          } />
          <Route path="/credentials" element={
            <PermissionRoute permissions={["credentials:read"]}>
              <CredentialsPage />
            </PermissionRoute>
          } />
          <Route path="/credentials/new" element={
            <PermissionRoute permissions={["credentials:write"]}>
              <CredentialFormPage />
            </PermissionRoute>
          } />
          <Route path="/credentials/:id/edit" element={
            <PermissionRoute permissions={["credentials:write"]}>
              <CredentialFormPage />
            </PermissionRoute>
          } />
          <Route path="/credential-types" element={
            <PermissionRoute permissions={["credential_types:read"]}>
              <CredentialTypesPage />
            </PermissionRoute>
          } />
          <Route path="/credential-types/new" element={
            <PermissionRoute permissions={["credential_types:write"]}>
              <CredentialTypeFormPage />
            </PermissionRoute>
          } />
          <Route path="/credential-types/:typeId/edit" element={
            <PermissionRoute permissions={["credential_types:write"]}>
              <CredentialTypeFormPage />
            </PermissionRoute>
          } />
          <Route path="/audit" element={
            <PermissionRoute permissions={["audit:view"]}>
              <AuditLogsPage />
            </PermissionRoute>
          } />
          <Route path="/pricing" element={
            <PermissionRoute permissions={["pricing:read"]}>
              <ModelPricingPage />
            </PermissionRoute>
          } />
          <Route path="/template-categories" element={
            <PermissionRoute permissions={["template-categories:read"]}>
              <TemplateCategoriesPage />
            </PermissionRoute>
          } />
          <Route path="/access-inspector" element={
            <PermissionRoute permissions={["access-inspector:read"]}>
              <AccessInspectorPage />
            </PermissionRoute>
          } />
        </Route>

        {/* Desk — lean agent workspace with its own minimal layout (no admin sidebar) */}
        <Route
          element={
            <ProtectedRoute>
              <DeskLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/desk" element={
            <PermissionRoute permissions={["helpdesk:read"]}>
              <DeskPage />
            </PermissionRoute>
          } />
          {/* Backward-compat: old Portuguese path → new English path */}
          <Route path="/atendimento" element={<Navigate to="/desk" replace />} />
        </Route>

        {/* Access denied route */}
        <Route path="/access-denied" element={
          <ProtectedRoute>
            <AccessDeniedPage />
          </ProtectedRoute>
        } />

        {/* Redirect root to dashboard */}
        <Route path="/" element={<LandingRedirect />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default AppRouter;
